// packages/web/src/api/notification-prefs/mutations.ts
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { meNotificationPrefsUpsert } from "../generated/sdk.gen.ts";
import { meNotificationPrefsOptions } from "./query-options.ts";

interface PrefItem {
  templateKey: string;
  channel: "email" | "push" | "inapp";
  enabled: boolean;
  isOverride: boolean;
}
interface PrefListData {
  items: PrefItem[];
}

export function useUpsertNotificationPref() {
  const qc = useQueryClient();
  const queryKey = meNotificationPrefsOptions().queryKey;

  return useMutation({
    mutationFn: async (vars: {
      templateKey: string;
      channel: "email" | "push" | "inapp";
      enabled: boolean;
    }) => {
      const { data, error } = await meNotificationPrefsUpsert({
        path: { templateKey: vars.templateKey, channel: vars.channel },
        body: { enabled: vars.enabled },
      });
      if (error) throw error;
      if (!data) throw new Error("meNotificationPrefsUpsert returned no data");
      return data;
    },
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey });
      const prev = qc.getQueryData<PrefListData>(queryKey);
      if (prev) {
        qc.setQueryData<PrefListData>(queryKey, {
          ...prev,
          items: prev.items.map((i) =>
            i.templateKey === vars.templateKey && i.channel === vars.channel
              ? { ...i, enabled: vars.enabled, isOverride: true }
              : i,
          ),
        });
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) {
        qc.setQueryData(queryKey, ctx.prev);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey });
    },
  });
}
